import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { mainAuth as auth } from '../auth/appAuth';
import {
  clearSecondFactorVerifiedSession,
  isSecondFactorVerifiedSession,
} from '../features/two-factor-auth/utils/pendingSecondFactorSession';
import { clearWebsiteOwnerSession, shouldPreserveWebsiteLocalStorageKey } from '../website/utils/websiteAuthSession';

const SESSION_KEY = 'lastSessionRefresh';
const INACTIVITY_THRESHOLD = 60 * 60 * 1000; // 1 hour in milliseconds

export const useSessionManager = () => {
  const { user } = useAuth();
  const location = useLocation();

  useEffect(() => {
    if (!auth) {
      console.warn('Auth context is not available');
      return;
    }

    if (!user) return;

    // App inactivity logout should not run on the marketing website or other non-app routes.
    if (!location.pathname.startsWith('/app')) return;

    // Website-only sessions without completed app 2FA must not trigger
    // app inactivity logout — it clears shared storage and signs out every tab.
    if (user.uid && !isSecondFactorVerifiedSession(user.uid)) return;

    let lastActivity = Date.now();
    let inactivityTimer;

    const resetInactivityTimer = () => {
      if (inactivityTimer) {
        clearTimeout(inactivityTimer);
      }

      const rememberMe =
        localStorage.getItem('rememberMe') === 'true' ||
        localStorage.getItem('mainAppRememberMe') === 'true' ||
        localStorage.getItem('websiteRememberMe') === 'true';

      if (!rememberMe) {
        inactivityTimer = setTimeout(handleInactivityTimeout, INACTIVITY_THRESHOLD);
      }
    };

    const updateLastActivity = () => {
      lastActivity = Date.now();
      localStorage.setItem(SESSION_KEY, lastActivity.toString());
      resetInactivityTimer();
    };

    const handleInactivityTimeout = async () => {
      console.log('User inactive, logging out...');
      await handleLogout();
    };

    const clearBrowserCache = async () => {
      if ('caches' in window) {
        try {
          const keys = await caches.keys();
          await Promise.all(keys.map(key => caches.delete(key)));
        } catch (error) {
          console.error('Error clearing cache:', error);
        }
      }
    };

    const handleLogout = async () => {
      try {
        await clearBrowserCache();
        clearSecondFactorVerifiedSession();
        clearWebsiteOwnerSession();

        Object.keys(localStorage).forEach((key) => {
          if (shouldPreserveWebsiteLocalStorageKey(key)) {
            return;
          }
          localStorage.removeItem(key);
        });

        sessionStorage.clear();

        await auth.signOut();

        window.location.href = '/app/login';
      } catch (error) {
        console.error('Logout error:', error);
        window.location.href = '/app/login';
      }
    };

    const activityEvents = [
      'mousedown',
      'mousemove',
      'keydown',
      'scroll',
      'touchstart',
      'click'
    ];

    activityEvents.forEach(event => {
      document.addEventListener(event, updateLastActivity);
    });

    updateLastActivity();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const now = Date.now();
        const lastActivityTime = parseInt(localStorage.getItem(SESSION_KEY), 10) || lastActivity;
        const isInactive = (now - lastActivityTime) > INACTIVITY_THRESHOLD;
        const rememberMe =
          localStorage.getItem('rememberMe') === 'true' ||
          localStorage.getItem('mainAppRememberMe') === 'true' ||
          localStorage.getItem('websiteRememberMe') === 'true';

        if (isInactive && !rememberMe) {
          handleInactivityTimeout();
        } else {
          updateLastActivity();
        }
      }
    };

    const handleOnlineStatus = () => {
      if (navigator.onLine) {
        const now = Date.now();
        const lastActivityTime = parseInt(localStorage.getItem(SESSION_KEY), 10) || lastActivity;
        const isInactive = (now - lastActivityTime) > INACTIVITY_THRESHOLD;
        const rememberMe =
          localStorage.getItem('rememberMe') === 'true' ||
          localStorage.getItem('mainAppRememberMe') === 'true' ||
          localStorage.getItem('websiteRememberMe') === 'true';

        if (isInactive && !rememberMe) {
          handleInactivityTimeout();
        } else {
          updateLastActivity();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnlineStatus);

    return () => {
      if (inactivityTimer) {
        clearTimeout(inactivityTimer);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnlineStatus);
      activityEvents.forEach(event => {
        document.removeEventListener(event, updateLastActivity);
      });
    };
  }, [user, location.pathname]);
};
